<script setup lang="ts">
import AccountPanelMissingVerificationAlert from '../../account/personal/AccountPanelPersonalMissingVerificationAlert.vue'
import AccountPanelPersonalMissingDataAlert from '../../account/personal/AccountPanelPersonalMissingDataAlert.vue'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import AccountPersonalOverviewPersonalData from '../../account/personal/overview/AccountPersonalOverviewPersonalData.vue'
import AccountPersonalOverviewAddressData from '../../account/personal/overview/AccountPersonalOverviewAddressData.vue'
import InvestmentProcessNavigationButtons from '../InvestmentProcessNavigationButtons.vue'
import { notify } from '../../../utilities/useNotifications'
import { i18n } from '../../../localization'
import {
    editAddressData,
    editCompanyData,
    editPersonalData,
    navigateToNextStep,
} from '../../../utilities/account/useInvestmentProcessNavigation'
import ECSPNaturalComplianceProfileOverview from '../ecsp/ECSPNaturalComplianceProfileOverview.vue'
import { isLegalProfile, isNaturalProfile } from '../../../utilities/investment-process/useUserProfileType'
import ECSPLegalComplianceProfileOverview from '../ecsp/ECSPLegalComplianceProfileOverview.vue'
import { isEcspComplianceProfileInformationMissingForInvestment } from '../../../utilities/account/useEcspRegulatory'
import AccountPanelLegalMissingDataAlert from '../../account/legal/AccountPanelLegalMissingDataAlert.vue'
import AccountPanelLegalMissingVerificationAlert from '../../account/legal/AccountPanelLegalMissingVerificationAlert.vue'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import AccountLegalOverviewCompanyData from '../../account/legal/overview/AccountLegalOverviewCompanyData.vue'
import AccountLegalOverviewAddressData from '../../account/legal/overview/AccountLegalOverviewAddressData.vue'
import { computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { trackAnalyticsInvestmentProcessStep2 } from '../../../utilities/useTracking'
import RightOfWithdrawalWarning from '../warning/RightOfWithdrawalWarning.vue'
import ConsequencesOfRevocationWarning from '../warning/ConsequencesOfRevocationWarning.vue'
import { getAccountLink } from '../../../utilities/useLinks'
import { getPlatformSettings } from '../../../utilities/usePlatformSettings'
import { useInvestmentProcessDataState } from '../../../utilities/investment-process/useInvestmentProcess'
import DepotAdditionalFieldsPanel from '../DepotAdditionalFieldsPanel.vue'

const store = usePersonalAccountDataState()
const legalStore = useLegalAccountDataState()
const { depotFields } = storeToRefs(useInvestmentProcessDataState())
const showWarningRightOfWithdrawal = computed(() => window?.showWarningRightOfWithdrawal)
const showWarningConsequencesOfRevocation = computed(() => window?.showWarningConsequencesOfRevocation)

const complianceProfile = computed(() => getPlatformSettings().compliance_profile)
const additionalFields = computed(() => window.campaign?.asset_data?.additional_fields ?? {})

onMounted(() => {
    trackAnalyticsInvestmentProcessStep2()
})

async function checkLegalRequirements() {
    if (
        (isNaturalProfile() && !store.hasAllRequiredNaturalProfileAttributesForInvestment) ||
        (isLegalProfile() && !legalStore.hasAllRequiredProfileAttributesForInvestment)
    ) {
        notify('Error', i18n.global.t('verification.complete_data_description'))
    } else if (
        (isNaturalProfile() && store.isVerified === false) ||
        (isLegalProfile() && legalStore.isVerified === false)
    ) {
        notify('Error', i18n.global.t('verification.complete_verification_outstanding'))
    } else if (await isEcspComplianceProfileInformationMissingForInvestment()) {
        notify('Error', i18n.global.t('compliance_profile.complete_data_description'))
    } else {
        const hasUnfilledRequired = Object.entries(additionalFields.value).some(
            ([field, meta]: [string, any]) => meta.required && !depotFields.value[field]?.trim()
        )
        if (hasUnfilledRequired) {
            notify('Error', i18n.global.t('investment_process.please_fill_required_fields'))
            return
        }
        navigateToNextStep()
    }
}

function goToVerificationPage() {
    window.location.href = getAccountLink() + '?menu=Verification'
}
</script>

<template>
    <div class="space-y-4 px-6 py-6">
        <AccountPanelPersonalMissingDataAlert
            v-if="isNaturalProfile() && !store.hasAllRequiredNaturalProfileAttributesForInvestment"
            class="shadow-none" />

        <AccountPanelMissingVerificationAlert
            v-if="isNaturalProfile() && !store.isVerified"
            @button-click="goToVerificationPage()" />

        <AccountPanelLegalMissingDataAlert
            v-if="isLegalProfile() && !legalStore.hasAllRequiredProfileAttributesForInvestment"
            class="shadow-none" />
        <AccountPanelLegalMissingVerificationAlert
            v-if="isLegalProfile() && !legalStore.isVerified"
            @button-click="goToVerificationPage()" />

        <div v-if="isNaturalProfile() && complianceProfile === 'ecsp'">
            <p class="pb-2 text-base font-semibold text-gray-600">
                {{ $t('account_settings.compliance_profile') }}
            </p>
            <ECSPNaturalComplianceProfileOverview />
        </div>
        <ECSPLegalComplianceProfileOverview v-if="isLegalProfile() && complianceProfile === 'ecsp'" />

        <div
            v-if="isNaturalProfile() && store.isVerified"
            class="overflow-hidden border border-gray-200 bg-white sm:col-span-1 sm:rounded-md">
            <AccountPersonalOverviewPersonalData @edit="editPersonalData" />
            <AccountPersonalOverviewAddressData @edit="editAddressData" />
        </div>
        <div
            v-else-if="isLegalProfile() && legalStore.isVerified"
            class="overflow-hidden border border-gray-200 bg-white sm:col-span-1 sm:rounded-md">
            <AccountLegalOverviewCompanyData @edit="editCompanyData" />
            <AccountLegalOverviewAddressData @edit="editAddressData" />
        </div>
        <DepotAdditionalFieldsPanel />
        <RightOfWithdrawalWarning v-if="showWarningRightOfWithdrawal" />
        <ConsequencesOfRevocationWarning v-if="showWarningConsequencesOfRevocation" />
        <InvestmentProcessNavigationButtons :on-next-click="checkLegalRequirements" />
    </div>
</template>

<style scoped></style>
